<template>
  <v-col class="register default-slot" md="7" sm="12">
    <v-dialog max-width="500px" v-model="rulesDialog">
      <v-card class="search-modal">
        <h2>Rules</h2>
        <ol class="text-left">
          <li>
            All sire and dam owners must have a valid email address on file to
            sign.
          </li>
          <li>
            All sires and dams must be DNA profiled prior to the registration of
            their first litter. You may submit your litter while DNA is pending
            and it will be approved once results are on file with ASCA.
          </li>
          <li>
            Please ensure all litter owners (owners/lessees of the dam), sire,
            dam and puppy listing displayed are correct prior to submitting your
            application. Corrections may require DNA testing (see Registry
            Rules) and/or submission of a signed paper litter application.
          </li>
          <li>
            If a dam or any of the sires are on lease on the litter whelping
            date, do not submit a Litter Application until the appropriate Lease
            Registrations are submitted and you are notified that the Lease is
            complete.
          </li>
          <li>
            If a dam is on lease on the litter whelping date, the primary lessee
            will be responsible for submitting the Litter Application with
            payment as well as registering and releasing puppies.
          </li>
        </ol>
      </v-card>
    </v-dialog>
    <div class="top-icons">
      <router-link class="back-icon" to="/">
        <font-awesome-icon icon="long-arrow-alt-left" size="lg" /> Back to home
      </router-link>
    </div>
    <h1 class="register-title">Litter Registration Application</h1>
    <v-alert
      class="mb-2 mt-1"
      v-if="!!damInfo && !!damInfo.dnaStatus && !damInfo.dnaSubmitted"
      type='warning'
      >      
        <span v-html="damDnaVerifiedWarning"></span>&nbsp;
        <span v-html="geneticTestingLink"></span>
    </v-alert>
    <v-alert
      class="mb-2 mt-1"
      v-if="validationInfo.errorMessage"
      :type="validationInfo.isRequestValid === true ? 'warning' : 'error'"
      >{{ validationInfo.errorMessage }}
    </v-alert>
      <v-row
        v-if="validationInfo.isRequestValid === true && validationInfo.isWhelpingDateValid === false"
        justify="center"
      >
        <v-text-field
          v-model="whelpingDateExplanation"
          @change="changeWhelpingDateExplanation($event)"
          label="Explanation for whelping date"
        ></v-text-field>
      </v-row>
    <p class="mt-3"><a v-on:click="toggleRules()">View rules</a> <br /></p>
    <v-form class="register-form">
      <v-row justify="center">
        <h3>Choose whelping date</h3>
      </v-row>
      <v-row class="mb-5" justify="center">
        <v-date-picker
          v-model="picker"
          :min="minDate"
          :max="maxDate"
        ></v-date-picker>
      </v-row>
      <v-row justify="center">
        <v-tooltip bottom content-class="fee-table-tooltip">
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">View pricing table</span>
          </template>
          <litter-fee-table :priceInfo="priceInfo" />
        </v-tooltip>
      </v-row>      
      <v-row>
          <v-col>
              <v-alert v-if="isArtificialInsemination" type="warning">
                  Sire’s collection information and <span v-html="artificialInseminationLink" /> must be on file with ASCA prior to approval of the litter.
                  Please send semen collection documentation from collection facility as well as AI forms directly to <span v-html="registrarEmailLink" />.
              </v-alert>
          </v-col>
      </v-row>
      <v-row justify="center">
        <v-checkbox
          v-model="isArtificialInsemination"
          label="Litter is from artificial insemination"
          class="align-center justify-center"
          @change="changeArtificialInsemination($event)"
        ></v-checkbox>
      </v-row>
      <v-row> </v-row>
    </v-form>
    <v-row>
      <v-col>
        <div v-if="damInfo.dogId">
          <h3>Dam Info</h3>
          <div>
            <p>Name: {{ damInfo.name }}</p>
            <p>ASCA ID: {{ damInfo.dogId }}</p>
            <p>Main Color: {{ damInfo.mainColor }}</p>
            <p>DNA Status: {{ damInfo.dnaStatus }}</p>
          </div>
        </div>
        <div v-else class="litter-placeholder" />
      </v-col>
      <v-col>
        <div v-if="!!damInfo && !!damInfo.primaryOwners">
          <div>
            <h3>Owners</h3>
            <div v-for="(owner, idx) in damInfo.primaryOwners" :key="idx">
              <span>
                <font-awesome-icon
                  icon="paw"
                  :color="owner.isPrimary ? 'blue' : 'transparent'"
                />&nbsp;
                <span
                  >{{ owner.firstName }} {{ owner.lastName }}
                  <span v-if="!owner.memberId">
                    (Member: {{ owner.memberId }})</span
                  ></span
                >
              </span>
              <br />
            </div>
            <v-divider></v-divider>
            <div v-if="!!damInfo && !!damInfo.lease">
              <h3>Lessees</h3>
              <div v-for="(owner, idx) in damInfo.lease.lesees" :key="idx">
                <span>
                  <font-awesome-icon
                    icon="paw"
                    :color="owner.isPrimary ? 'blue' : 'transparent'"
                  />&nbsp;
                  <span
                    >{{ owner.firstName }} {{ owner.lastName }}
                    <span v-if="!owner.memberId">
                      (Member: {{ owner.memberId }})</span
                    ></span
                  >
                </span>
                <br />
              </div>
              <v-divider></v-divider>
            </div>
            <div class="text-center" v-if="damInfo.ownersKennels.length">
              <h3>Kennels that will be associated with this litter:
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon color="darkGray" class="ml-1" dark v-bind="attrs" v-on="on">
                      mdi-information
                    </v-icon>
                  </template>
                  <span>Titles obtained by puppies from this litter will count toward HOF (Hall of Fame), HOFX (Hall of Fame Excellent) and SKE (Stockdog Kennel of Excellence) for associated Kennels.</span>
                </v-tooltip>
              </h3>
              <list v-for="kennel in damInfo.ownersKennels" :key="kennel.id">
                <ul>
                  <v-icon>mdi-home</v-icon>{{kennel.name}}
                </ul>
              </list>
            </div>
          </div>
        </div>
        <div v-else class="litter-placeholder" />
      </v-col>
    </v-row>

    <v-btn
      class="white--text"
      large
      v-on:click="changePage(2)"
      :disabled="!isValid"
      color="primary"
      >NEXT</v-btn
    >
    <p class="steps">STEP <b>1</b> OF <b>3</b></p>
    <footer-text />
  </v-col>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import { debounce } from "throttle-debounce";
import { UserInfo } from "@/types/User";
import LitterFeeTable from "@/components/Portal/LitterFeeTable.vue";
import FooterText from "@/components/common/FooterText.vue";

import { LitterRegistration } from "@/constants";

export default Vue.extend({
  name: "RegisterLitterForm1",
  data: function () {
    return {
      rulesDialog: false,
      damDnaVerifiedWarning: LitterRegistration.MissingDNA_Dam,    
      geneticTestingLink: LitterRegistration.GeneticTesting_Link,
      artificialInseminationLink: LitterRegistration.ArtificialInsemination_Link,
      registrarEmailLink: LitterRegistration.RegistrarEmail_Link,
    };
  },
  methods: {
    toggleRules: function () {
      this.$data.rulesDialog = !this.$data.rulesDialog;
    },
  },
  computed: {
    userInfo(): UserInfo {
      return this.$store.state.userInfo;
    },
    isValid: function () {
      //request needs to be validated
      //if whelping date is invalid, then explanation has to be provided
      return (
        (this as any).validationInfo.isRequestValid &&
        ((this as any).validationInfo.isWhelpingDateValid ||
          (this as any).whelpingDateExplanation)
      );
    },
  },
  watch: {
    picker: {
      handler: debounce(
        500,
        function (this: any, newVal: string, oldVal: string) {
          if (newVal === oldVal) return;
          if (newVal) {
            this.changeDate(newVal);
            this.validateWhelpingDate(
              this.userInfo.personId,
              this.dogId,
              newVal
            );
            this.searchDam(this.dogId, new Date(newVal));
          }
        }
      ),
    },
  },
  props: {
    changePage: Function,
    damInfo: Object,
    searchDam: Function,
    dogId: String,
    picker: String,
    changeDate: Function,
    whelpingDateExplanation: String,
    isArtificialInsemination: Boolean,
    changeWhelpingDateExplanation: Function,
    changeArtificialInsemination: Function,
    validateWhelpingDate: Function,
    validationInfo: Object,
    minDate: { type: [null, String] as PropType<null | string> },
    maxDate: { type: [null, String] as PropType<null | string> },
    priceInfo: Array,
  },
  components: {
    LitterFeeTable,
    FooterText,
  },
});
</script>
